import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Search, Edit, Trash2, Eye, UserPlus, Shield, ShieldOff,
    Phone, Calendar, Check
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { adminApi } from '../../lib/api'

interface User {
    id: number
    email: string
    full_name: string
    phone: string
    role: string
    verified: boolean
    created_at: string
}

interface Stats {
    total: number
    customers: number
    owners: number
    admins: number
    unverified: number
}

export default function UserManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [roleFilter, setRoleFilter] = useState('all')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedUser, setSelectedUser] = useState<User | null>(null)
    const [users, setUsers] = useState<User[]>([])
    const [stats, setStats] = useState<Stats>({ total: 0, customers: 0, owners: 0, admins: 0, unverified: 0 })

    const roles = [
        { id: 'all', name: { en: 'All', vi: 'Tất cả' } },
        { id: 'customer', name: { en: 'Customers', vi: 'Khách hàng' } },
        { id: 'owner', name: { en: 'Owners', vi: 'Chủ xe' } },
        { id: 'admin', name: { en: 'Admins', vi: 'Quản trị' } },
    ]

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const params: any = { search: searchQuery }
                if (roleFilter !== 'all') {
                    params.role = roleFilter
                }
                const response = await adminApi.getUsers(params)
                if (response.data?.data) {
                    setUsers(response.data.data)
                }
                if (response.data?.stats) {
                    setStats(response.data.stats)
                }
            } catch (error) {
                console.error('Failed to fetch users:', error)
            }
        }
        fetchUsers()
    }, [roleFilter, searchQuery])

    const handleVerify = async (userId: number) => {
        try {
            await adminApi.verifyUser(userId)
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, verified: true } : u))
            alert(language === 'en' ? 'User verified!' : 'Đã xác minh!')
        } catch (error) {
            console.error('Failed to verify:', error)
            alert(language === 'en' ? 'Failed to verify' : 'Lỗi khi xác minh')
        }
    }

    const handleDelete = async () => {
        if (!selectedUser) return
        try {
            await adminApi.deleteUser(selectedUser.id)
            setUsers(prev => prev.filter(u => u.id !== selectedUser.id))
            setShowDeleteModal(false)
            setSelectedUser(null)
            alert(language === 'en' ? 'User deleted!' : 'Đã xóa người dùng!')
        } catch (error) {
            console.error('Failed to delete:', error)
            alert(language === 'en' ? 'Failed to delete' : 'Lỗi khi xóa')
        }
    }

    const filteredUsers = users

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {language === 'en' ? 'User Management' : 'Quản Lý Người Dùng'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {language === 'en' ? 'Manage all platform users' : 'Quản lý tất cả người dùng'}
                    </p>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition">
                    <UserPlus className="w-5 h-5" />
                    {language === 'en' ? 'Add User' : 'Thêm người dùng'}
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total Users' : 'Tổng số'}</p>
                    <p className="text-2xl font-bold mt-1">{stats.total}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Customers' : 'Khách hàng'}</p>
                    <p className="text-2xl font-bold mt-1 text-blue-600">{stats.customers}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Owners' : 'Chủ xe'}</p>
                    <p className="text-2xl font-bold mt-1 text-green-600">{stats.owners}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Unverified' : 'Chưa xác thực'}</p>
                    <p className="text-2xl font-bold mt-1 text-orange-600">{stats.unverified}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="search"
                            placeholder={language === 'en' ? 'Search by name or email...' : 'Tìm theo tên hoặc email...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="flex gap-2">
                        {roles.map((role) => (
                            <button
                                key={role.id}
                                onClick={() => setRoleFilter(role.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${roleFilter === role.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {role.name[language]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    <input type="checkbox" className="rounded" />
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'User' : 'Người dùng'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                                    {language === 'en' ? 'Contact' : 'Liên hệ'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                                    {language === 'en' ? 'Role' : 'Vai trò'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                                    {language === 'en' ? 'Status' : 'Trạng thái'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                                    {language === 'en' ? 'Joined' : 'Ngày tham gia'}
                                </th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Actions' : 'Thao tác'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <input type="checkbox" className="rounded" />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center font-medium">
                                                {user.full_name?.charAt(0) || 'U'}
                                            </div>
                                            <div>
                                                <p className="font-medium">{user.full_name}</p>
                                                <p className="text-sm text-gray-500">{user.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden md:table-cell">
                                        <div className="flex items-center gap-1 text-sm text-gray-600">
                                            <Phone className="w-4 h-4" />
                                            {user.phone}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                                            user.role === 'owner' ? 'bg-green-100 text-green-700' :
                                                'bg-blue-100 text-blue-700'
                                            }`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        {user.verified ? (
                                            <span className="flex items-center gap-1 text-green-600 text-sm">
                                                <Shield className="w-4 h-4" />
                                                {language === 'en' ? 'Verified' : 'Đã xác thực'}
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-orange-600 text-sm">
                                                <ShieldOff className="w-4 h-4" />
                                                {language === 'en' ? 'Unverified' : 'Chưa xác thực'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 hidden md:table-cell text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {user.created_at}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button className="p-2 hover:bg-gray-100 rounded-lg" title="View">
                                                <Eye className="w-4 h-4 text-gray-600" />
                                            </button>
                                            <button className="p-2 hover:bg-blue-50 rounded-lg" title="Edit">
                                                <Edit className="w-4 h-4 text-blue-600" />
                                            </button>
                                            {!user.verified && (
                                                <button
                                                    onClick={() => handleVerify(user.id)}
                                                    className="p-2 hover:bg-green-50 rounded-lg"
                                                    title="Verify"
                                                >
                                                    <Check className="w-4 h-4 text-green-600" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setSelectedUser(user)
                                                    setShowDeleteModal(true)
                                                }}
                                                className="p-2 hover:bg-red-50 rounded-lg"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-bold mb-2">
                            {language === 'en' ? 'Delete User' : 'Xóa Người Dùng'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {language === 'en'
                                ? `Are you sure you want to delete "${selectedUser?.full_name}"?`
                                : `Bạn có chắc muốn xóa "${selectedUser?.full_name}"?`
                            }
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                {language === 'en' ? 'Cancel' : 'Hủy'}
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                {language === 'en' ? 'Delete' : 'Xóa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
